import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  EnergyTrainingZone,
  EnergyTrainingZoneRange,
} from "../../data/consultationTypes";
import PillTabs from "./PillTabs";

/**
 * Energy & Training zones chart — the unit pickers, the selected-zone card,
 * the fat/carb area chart and the zone pill row. Per Figma nodes
 * 14711:148285 (no zone selected) and 14711:148819 (zone detail card
 * visible above the chart).
 *
 * Shared by the consultation's Energy & Training panel and the Health
 * Overview's Energy & Training Zones system; both pass the `GetConsultation`
 * `energyTraining.zones`. Renders a fragment so the parent's flex column
 * owns the spacing between the rows. Renders nothing without zones — the
 * parent owns the empty state.
 *
 * The two TabsContainers (HR / Watts / Speed and kcal/min / gr/min) toggle
 * the units shown in the zone-detail card and the chart x-axis labels.
 */

type AxisUnit = "Heart Rate" | "Watts" | "Speed";
type ValueUnit = "kcal/min" | "gr/min";

type ZoneRange = { lower: number; upper: number; label: string };

type Zone = {
  zone: number;
  ranges: Record<AxisUnit, ZoneRange>;
  // Backend ships fat/carb as percentages and grams/min directly. Total
  // kcal/min is derived client-side from grams via the standard
  // 9 kcal/g fat + 4 kcal/g carb conversion.
  fatPct: number;
  carbPct: number;
  fatGrams: number;
  carbGrams: number;
  fatKcal: number; // derived: fatGrams * 9
  carbKcal: number; // derived: carbGrams * 4
};

/** Unit suffix per axis tab (used in the zone card line + x-axis tick text). */
const AXIS_UNIT: Record<AxisUnit, string> = {
  "Heart Rate": "bpm",
  Watts: "W",
  Speed: "km/h",
};

/**
 * Chart layout constants shared between the Recharts `YAxis` / chart-margin
 * props and the zone-row's `paddingLeft` / `paddingRight`. Locking them on
 * both sides is the only way to keep each zone pill the same width as the
 * matching zone segment in the chart — Recharts' default YAxis width
 * auto-adjusts to label length, which would silently break alignment if
 * the largest Y-axis label ever changes.
 */
const Y_AXIS_WIDTH = 60;
const CHART_RIGHT_MARGIN = 8;

/** 1 decimal place — matches Figma "0.5 kcal/min". */
const fmtKcal = (n: number): string =>
  Number.isFinite(n) ? `${n.toFixed(1)} kcal/min` : "—";

/** 2 decimal places — gram-per-minute values are small (~0.05 g/min). */
const fmtGrams = (n: number): string =>
  Number.isFinite(n) ? `${n.toFixed(2)} g/min` : "—";

/** An unmeasured (`null`) range reads as 0 – 0, which hides its axis tab. */
const toZoneRange = (r: EnergyTrainingZoneRange): ZoneRange => {
  const lower = r?.lower ?? 0;
  const upper = r?.upper ?? 0;
  return { lower, upper, label: `${lower} - ${upper}` };
};

type Props = {
  zones: EnergyTrainingZone[];
};

const EnergyTrainingChart: React.FC<Props> = ({ zones: rawZones }) => {
  const theme: Theme = useTheme();
  const [active, setActive] = useState(1);
  const [axisUnit, setAxisUnit] = useState<AxisUnit>("Heart Rate");
  const [valueUnit, setValueUnit] = useState<ValueUnit>("kcal/min");

  const zones = useMemo<Zone[]>(() => {
    // Backend ships fat/carb percentages directly and grams/min for the
    // mass breakdown. We derive kcal/min from grams using the standard
    // nutrition conversion (9 kcal/g fat, 4 kcal/g carb) — see
    // CONSULTATION_BACKEND_FIXES_V1.md #9.
    return rawZones.map((m) => {
      const fatGrams = Number(m.fatGrams) || 0;
      const carbGrams = Number(m.carbGrams) || 0;
      return {
        zone: m.zone,
        ranges: {
          "Heart Rate": toZoneRange(m.hr),
          Watts: toZoneRange(m.watts),
          Speed: toZoneRange(m.speed),
        },
        fatPct: Number(m.fat) || 0,
        carbPct: Number(m.carb) || 0,
        fatGrams,
        carbGrams,
        fatKcal: fatGrams * 9,
        carbKcal: carbGrams * 4,
      };
    });
  }, [rawZones]);

  // Axis-options gating. Some clients don't have a watts or speed source
  // (treadmill-only test, cycle-only test, etc.); for those, the backend
  // ships every zone's `watts` or `speed` range as `0 / 0`. We hide axis
  // tabs whose every zone is `0 / 0` so the picker only offers options
  // the data can actually back. If only one axis has data, the picker
  // collapses entirely (see render below).
  const availableAxisUnits = useMemo<AxisUnit[]>(() => {
    if (zones.length === 0) return [];
    const ALL: AxisUnit[] = ["Heart Rate", "Watts", "Speed"];
    return ALL.filter((unit) =>
      zones.some(
        (z) => z.ranges[unit].upper !== 0 || z.ranges[unit].lower !== 0
      )
    );
  }, [zones]);

  // If the currently-selected axis isn't in the available list (e.g.
  // initial state was "Heart Rate" but for some edge-case client HR is
  // missing and only Watts has data), snap to the first available axis.
  // No-op when the current axis is still valid.
  useEffect(() => {
    if (
      availableAxisUnits.length > 0 &&
      !availableAxisUnits.includes(axisUnit)
    ) {
      setAxisUnit(availableAxisUnits[0]);
    }
  }, [availableAxisUnits, axisUnit]);

  // Chart data points at every zone boundary so the area chart renders
  // smooth transitions between zones. The `x` value is in the units of the
  // currently active axis tab (bpm / W / km/h).
  const chartData = useMemo(() => {
    if (zones.length === 0) return [];
    const points: { x: number; fat: number; carb: number }[] = [];
    zones.forEach((z) => {
      points.push({
        x: z.ranges[axisUnit].lower,
        fat: z.fatPct,
        carb: z.carbPct,
      });
    });
    const last = zones[zones.length - 1];
    points.push({
      x: last.ranges[axisUnit].upper,
      fat: last.fatPct,
      carb: last.carbPct,
    });
    return points;
  }, [zones, axisUnit]);

  // Sum of all zone spans on the active axis — used by the pill row below
  // to decide between "Zone N" (wide pill) and "ZN" (narrow pill) labels.
  const totalSpan = zones.reduce(
    (s, z) =>
      s + Math.max(1, z.ranges[axisUnit].upper - z.ranges[axisUnit].lower),
    0
  );

  if (zones.length === 0) return null;

  const current = zones.find((z) => z.zone === active) ?? zones[0];

  return (
    <>
      <Box sx={styles.tabsRow}>
        {/* HR / Watts / Speed picker — only render when more than one axis
            has zone data. Single-axis case (e.g. HR-only clients) hides
            the picker entirely; a `<Box />` placeholder keeps the kcal/gr
            picker right-aligned via the row's `space-between`. */}
        {availableAxisUnits.length > 1 ? (
          <PillTabs
            options={availableAxisUnits}
            active={axisUnit}
            onSelect={(v) => setAxisUnit(v as AxisUnit)}
          />
        ) : (
          <Box />
        )}
        <PillTabs
          options={["kcal/min", "gr/min"]}
          active={valueUnit}
          onSelect={(v) => setValueUnit(v as ValueUnit)}
        />
      </Box>

      <Box sx={styles.zoneCard(theme)}>
        <Box sx={styles.zoneCardTopRow}>
          <Box component="span" sx={styles.zoneCardTitle(theme)}>
            Zone {current.zone}
          </Box>
          <Box component="span" sx={styles.zoneCardRange(theme)}>
            {current.ranges[axisUnit].label} {AXIS_UNIT[axisUnit]}
          </Box>
        </Box>
        <Box sx={styles.zoneCardStats}>
          <Box sx={styles.legendItem}>
            <Box sx={styles.legendDot(theme.colorTokens.P[60])} />
            <Box component="span" sx={styles.legendLabel(theme)}>
              Fat burn
            </Box>
            <Box component="span" sx={styles.zoneStatValue(theme)}>
              {current.fatPct}%
            </Box>
            <Box component="span" sx={styles.zoneStatKcal(theme)}>
              {valueUnit === "kcal/min"
                ? fmtKcal(current.fatKcal)
                : fmtGrams(current.fatGrams)}
            </Box>
          </Box>
          <Box sx={styles.legendItem}>
            <Box sx={styles.legendDot(theme.colors_ds.SUCCESS_ICON_MAIN)} />
            <Box component="span" sx={styles.legendLabel(theme)}>
              Carb burn
            </Box>
            <Box component="span" sx={styles.zoneStatValue(theme)}>
              {current.carbPct}%
            </Box>
            <Box component="span" sx={styles.zoneStatKcal(theme)}>
              {valueUnit === "kcal/min"
                ? fmtKcal(current.carbKcal)
                : fmtGrams(current.carbGrams)}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="fatBurnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.colorTokens.P[60]}
                  stopOpacity={0.4}
                />
                <stop
                  offset="100%"
                  stopColor={theme.colorTokens.P[60]}
                  stopOpacity={0.05}
                />
              </linearGradient>
              <linearGradient id="carbBurnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.colors_ds.SUCCESS_ICON_MAIN}
                  stopOpacity={0.4}
                />
                <stop
                  offset="100%"
                  stopColor={theme.colors_ds.SUCCESS_ICON_MAIN}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="x"
              type="number"
              domain={["dataMin", "dataMax"]}
              ticks={zones
                .map((z) => z.ranges[axisUnit].lower)
                .concat([zones[zones.length - 1].ranges[axisUnit].upper])}
              // `interval={0}` forces Recharts to render every tick in the
              // `ticks` array. Without it, Recharts may auto-collapse ticks
              // whose labels visually overlap — which would leave some zone
              // boundaries without a value below the chart. We want one
              // value per zone boundary (always).
              interval={0}
              tickFormatter={(v) =>
                v === zones[zones.length - 1].ranges[axisUnit].upper
                  ? `${v}+`
                  : `${v}`
              }
              stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
              tick={{ fontSize: 11, fill: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
            />
            <YAxis
              tickFormatter={(v) => `${v}%`}
              ticks={[0, 25, 50, 75, 100]}
              domain={[0, 100]}
              // Lock the YAxis width so the plot area's left edge is
              // predictable — the zone row below uses the same value as
              // `paddingLeft` to align with the chart, and Recharts'
              // default (60) auto-adjusts to label width otherwise.
              width={Y_AXIS_WIDTH}
              stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
              tick={{ fontSize: 11, fill: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
            />
            {/* `stepAfter`: each zone has a constant value (e.g. fat = 75%
                throughout Zone 1 [100→120 bpm]). The line stays horizontal
                at the current value until it hits the next zone boundary,
                then jumps to the new value. This tells the truth about the
                data — fat/carb % aren't smoothly interpolating between
                zones, they're piecewise-constant per zone.

                `stackId` makes fat + carb visually sum to 100% at every x:
                fat fills 0→fatPct, carb stacks above to 100%. */}
            <Area
              type="stepAfter"
              dataKey="fat"
              stackId="burn"
              stroke={theme.colorTokens.P[60]}
              fill="url(#fatBurnGrad)"
              strokeWidth={2}
              activeDot={false}
            />
            <Area
              type="stepAfter"
              dataKey="carb"
              stackId="burn"
              stroke={theme.colors_ds.SUCCESS_ICON_MAIN}
              fill="url(#carbBurnGrad)"
              strokeWidth={2}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* CSS grid (not flex) is the right tool here. Flex with
          `flex-basis: 0; flex-grow: span` looks proportional but the
          algorithm inflates each item's hypothetical main size to its
          min-content, then distributes the remainder by flex-grow — so
          per-item overhead "leaks" into the wider zones disproportionately
          (verified empirically: 18px drift on Zone 3 before this change).
          Grid `fr` units distribute the FULL track width strictly by
          fraction, ignoring item content sizes. Combined with
          `minWidth: 0` on each pill (so the pill doesn't insist on
          fitting its label), this gives pixel-perfect alignment with
          the chart's linear x-scale. */}
      <Box
        sx={{
          ...styles.zoneRow,
          gridTemplateColumns: zones
            .map((z) => {
              const r = z.ranges[axisUnit];
              return `${Math.max(1, r.upper - r.lower)}fr`;
            })
            .join(" "),
        }}
      >
        {zones.map((z, i) => {
          const range = z.ranges[axisUnit];
          const span = Math.max(1, range.upper - range.lower);
          // Compact label only when the pill is too narrow for "Zone N".
          // Threshold: zones below 10% of total span fall back to "ZN"
          // (matches the mobile Figma); wider pills keep "Zone N".
          const ratio = totalSpan > 0 ? span / totalSpan : 0;
          const label = ratio < 0.1 ? `Z${z.zone}` : `Zone ${z.zone}`;
          // Only the outermost pills get rounded corners. Middle pills
          // are sharp on all sides so adjacent pills meet seamlessly
          // (no corner cutouts visible between them).
          const position: "first" | "middle" | "last" =
            i === 0 ? "first" : i === zones.length - 1 ? "last" : "middle";
          return (
            <Box
              key={z.zone}
              component="button"
              type="button"
              onClick={() => setActive(z.zone)}
              data-agent-target={`energy-zone-${z.zone}`}
              sx={styles.zonePill(theme, z.zone === active, position)}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default EnergyTrainingChart;

const styles = {
  tabsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  legendDot: (color: string) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: color,
  }),
  legendLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  zoneCard: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.PRIMARY_TEXT_MAIN}`,
    borderRadius: "8px",
    padding: "13px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  }),
  zoneCardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  zoneCardTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  zoneCardRange: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  zoneCardStats: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  zoneStatValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  zoneStatKcal: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "20px",
    letterSpacing: "-0.15px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  chartWrap: {
    width: "100%",
  },
  zoneRow: {
    // CSS grid with `gridTemplateColumns: <span>fr <span>fr ...` set
    // dynamically per zone. Tracks distribute the row's content width
    // strictly proportionally — unlike flex, grid `fr` units ignore
    // item content sizes entirely, so widths exactly match the chart's
    // linear x-scale.
    display: "grid",
    gap: "0",
    alignItems: "center",
    width: "100%",
    // Mirror the chart's plot-area bounds: YAxis on the left, right
    // chart margin on the right. Uses the same constants the YAxis
    // component reads so the two stay in lockstep.
    paddingLeft: `${Y_AXIS_WIDTH}px`,
    paddingRight: `${CHART_RIGHT_MARGIN}px`,
    boxSizing: "border-box" as const,
    // Pulls the zone-pill strip 20px closer to the chart. Negative
    // margin is OK here — the pills sit in flow but visually overlap
    // the chart's bottom whitespace (below the x-axis ticks).
    marginTop: "-20px",
  },
  zonePill: (
    theme: Theme,
    active: boolean,
    position: "first" | "middle" | "last"
  ) => ({
    cursor: "pointer",
    // `box-sizing: border-box` keeps the pill's 1px border + 8px
    // horizontal padding INSIDE the column width grid assigns — without
    // it the pill would overflow its grid track and break alignment.
    boxSizing: "border-box" as const,
    // `minWidth: 0` overrides grid's `min-width: auto` default, which
    // would otherwise expand the column to fit the pill's intrinsic
    // content. Without this, narrow zones (e.g. Z5 = 8 units) would
    // grow their column to fit "Z5", stealing space from wider zones
    // and breaking chart alignment. The fallback to "Z1..Z5" labels
    // (see render) keeps the compact text legible even at small widths.
    minWidth: 0,
    border: `1px solid ${
      active
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: active ? theme.colorTokens.P[10] : theme.colorTokens.N[40],
    borderRadius: "8px",
    // 3px horizontal margin on each side creates a 6px visual gap
    // between adjacent pills. The OUTERMOST pills (first / last) drop
    // their outer-edge margin so the strip stays flush with the chart's
    // left and right edges. Critically: margin is applied INSIDE the
    // grid track (not via grid `gap`), so the column widths remain
    // exactly proportional to zone spans — the pill shrinks within its
    // track but the track itself still aligns with the chart zone.
    marginLeft: position === "first" ? 0 : "3px",
    marginRight: position === "last" ? 0 : "3px",
    padding: "4px 8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: active
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as const,
    textAlign: "center" as const,
    // Hide overflow so a narrow column can clip the label rather than
    // expand the track. Combined with the "Z<N>" fallback for zones
    // <10% of total, this almost never trims real content.
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};
